import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Checkbox,
    FormControlLabel,
    Button,
    useTheme,
    Grid,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCreateAcademicTerm } from '../../hooks/academicTerms/useCreateAcademicTerm';
import { useUpdateAcademicTerm } from '../../hooks/academicTerms/useUpdateAcademicTerm';
import { useGetAcademicYears } from '../../hooks/academicYears/useGetAcademicYears';
import FormInput from '../inputs/FormInput';
import LoadingPage from '../global/LoadingPage';

const FormAddEditAcademicTerm = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const theme = useTheme();
    const lng = i18n.language || 'en';
    const isArabic = lng === 'ar';
    const isDark = theme.palette.mode === 'dark';

    const { createAcademicTerm, isPending: isCreating } = useCreateAcademicTerm();
    const { editAcademicTerm, isUpdate } = useUpdateAcademicTerm();
    const { isLoading: yearsLoading, academicYears } = useGetAcademicYears(1, 999);

    const loadingSubmit = isCreating || isUpdate;

    const [form, setForm] = useState({
        academic_year_id: '',
        code: '',
        name: '',
        start_date: '',
        end_date: '',
        is_current: false,
        is_active: true,
    });

    useEffect(() => {
        if (!isEdit || !currentData) return;
        setForm({
            academic_year_id: currentData.academic_year_id || '',
            code: currentData.code || '',
            name: currentData.name || '',
            start_date: currentData.start_date || '',
            end_date: currentData.end_date || '',
            is_current: currentData.is_current ?? false,
            is_active: currentData.is_active ?? true,
        });
    }, [isEdit, currentData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (type === 'checkbox') {
            setForm((prev) => ({ ...prev, [name]: checked }));
            return;
        }
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleYearChange = (e) => {
        setForm((prev) => ({ ...prev, academic_year_id: e.target.value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            academic_year_id: Number(form.academic_year_id),
            code: form.code || null,
            name: form.name,
            start_date: form.start_date,
            end_date: form.end_date,
            is_current: !!form.is_current,
            is_active: !!form.is_active,
        };

        if (isEdit && currentData?.id) {
            editAcademicTerm({ req: payload, id: currentData.id });
        } else {
            createAcademicTerm(payload);
        }
    };

    if (yearsLoading) return <LoadingPage />;

    const yearOptions = (academicYears || []).map((y) => ({ id: y.id, name: y.name }));

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? 'rtl' : 'ltr',
                backgroundColor: isDark && 'transparent',
            }}
        >
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'السنة الدراسية' : 'Academic Year'}
                            type="select"
                            name="academic_year_id"
                            value={form.academic_year_id}
                            onChange={handleYearChange}
                            options={yearOptions}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'كود الفصل' : 'Term Code'}
                            placeholder={isArabic ? 'مثال: T1' : 'e.g. T1'}
                            type="text"
                            name="code"
                            value={form.code}
                            onChange={handleChange}
                            required={false}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? 'اسم الفصل الدراسي' : 'Term Name'}
                            placeholder={isArabic ? 'ادخل اسم الفصل' : 'Enter term name'}
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormInput
                            label={isArabic ? 'تاريخ البداية' : 'Start Date'}
                            type="date"
                            name="start_date"
                            value={form.start_date}
                            onChange={handleChange}
                        />
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <FormInput
                            label={isArabic ? 'تاريخ النهاية' : 'End Date'}
                            type="date"
                            name="end_date"
                            value={form.end_date}
                            onChange={handleChange}
                            min={form.start_date}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: { xs: 1, md: 1 } }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.is_current}
                                        name="is_current"
                                        onChange={handleChange}
                                        size="small"
                                        color="warning"
                                    />
                                }
                                label={isArabic ? 'الفصل الحالي' : 'Current Term'}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.is_active}
                                        name="is_active"
                                        onChange={handleChange}
                                        size="small"
                                    />
                                }
                                label={isArabic ? 'مفعّل' : 'Active'}
                            />
                        </Box>
                    </Grid>

                    {form.is_current && (
                        <Grid item xs={12}>
                            <Alert severity="warning" variant="outlined">
                                {isArabic
                                    ? 'تنبيه: تحديد هذا الفصل كحالي سيُزيل علامة "الحالي" من أي فصل دراسي آخر في نفس السنة الدراسية تلقائياً.'
                                    : 'Warning: Setting this as the current term will automatically unset the "current" flag from any other term in the same academic year.'}
                            </Alert>
                        </Grid>
                    )}
                </Grid>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loadingSubmit}
                    >
                        {loadingSubmit
                            ? isArabic ? 'جاري الحفظ...' : 'Saving...'
                            : isEdit
                                ? isArabic ? 'تحديث' : 'Update'
                                : isArabic ? 'حفظ' : 'Save'}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default FormAddEditAcademicTerm;
